import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';
import { QrCode } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  원본 stampfit_refined_no_certificate_with_faq.html 의 디자인 토큰     */
/*  (기존 프로젝트 Navy/Orange 브랜드와 독립적으로 원본 색·폰트·크기 사용)  */
/* ------------------------------------------------------------------ */

const INK = '#101522';
const MUTED = '#4b5563';
const ACCENT = '#d81745';
const ACCENT_SOFT = '#fff0f3';
const LINE = '#e8ecf2';
const DEEP_GRADIENT = 'linear-gradient(135deg, #101522, #1b2433)';

/* ------------------------------------------------------------------ */
/*  스크롤 리빌 래퍼 — IntersectionObserver 기반 페이드/슬라이드 인        */
/* ------------------------------------------------------------------ */

type RevealVariant = 'up' | 'left' | 'right' | 'scale' | 'fade';

interface RevealProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  variant?: RevealVariant;
  delay?: number;
  as?: ElementType;
  once?: boolean;
}

const VARIANT_HIDDEN: Record<RevealVariant, string> = {
  up: 'opacity-0 translate-y-12',
  left: 'opacity-0 -translate-x-12',
  right: 'opacity-0 translate-x-12',
  scale: 'opacity-0 scale-90',
  fade: 'opacity-0',
};

function Reveal({
  children,
  className = '',
  style,
  variant = 'up',
  delay = 0,
  as: Tag = 'div',
  once = true,
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [once]);

  return (
    <Tag
      ref={ref as never}
      style={{ transitionDelay: `${delay}ms`, ...style, willChange: visible ? 'auto' : 'transform, opacity' }}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        visible ? 'opacity-100 translate-x-0 translate-y-0 scale-100' : VARIANT_HIDDEN[variant]
      } ${className}`}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ 아코디언 (원본 + / − 마커)                                       */
/* ------------------------------------------------------------------ */

function FaqItem({ question, children }: { question: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="overflow-hidden rounded-[22px] bg-white transition-all duration-300"
      style={{
        border: `1px solid ${LINE}`,
        boxShadow: open
          ? '0 16px 44px rgba(216,23,69,0.10)'
          : '0 10px 30px rgba(15,23,42,0.05)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex w-full items-center justify-between gap-4 px-[30px] py-7 text-left"
        aria-expanded={open}
      >
        <span className="text-[17px] font-extrabold sm:text-[20px]" style={{ color: INK }}>
          {question}
        </span>
        <span
          className="shrink-0 select-none text-[32px] leading-none transition-transform duration-300"
          style={{ color: ACCENT }}
        >
          {open ? '−' : '+'}
        </span>
      </button>
      <div
        className="grid transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div
            className={`px-[30px] pb-[30px] text-[16px] leading-[1.7] transition-opacity duration-500 ${
              open ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ color: MUTED }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  메인                                                                */
/* ------------------------------------------------------------------ */

interface AboutStampFitProps {
  /** "교육 둘러보기" 클릭 시 — 교육 메인 탭으로 이동 */
  onExploreEducation?: () => void;
  /** "채용 둘러보기" 클릭 시 — 구인구직 페이지로 이동 */
  onExploreJobs?: () => void;
  /** "교육 기관 입점 신청" 클릭 시 — 우수 인증 교육기관 탭으로 이동 */
  onExplorePremium?: () => void;
  /** "강사로 시작하기" 클릭 시 — 로그인 여부에 따라 교육 메인 탭 또는 일반 회원 로그인으로 이동 */
  onStartInstructor?: () => void;
  /** "채용 등록하기" 클릭 시 — 로그인 여부에 따라 새 구인글 등록 또는 기업 회원 로그인으로 이동 */
  onRegisterJob?: () => void;
}

export default function AboutStampFit({
  onExploreEducation,
  onExploreJobs,
  onExplorePremium,
  onStartInstructor,
  onRegisterJob,
}: AboutStampFitProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div
      className="overflow-hidden"
      style={{
        fontFamily: '"Pretendard", "Noto Sans KR", sans-serif',
        color: INK,
        lineHeight: 1.7,
        // 원본 standalone HTML과 동일한 글자 렌더링(앱 전역의 antialiased는 글자를 얇게 만듦)
        WebkitFontSmoothing: 'auto',
        MozOsxFontSmoothing: 'auto',
        background:
          'radial-gradient(circle at 10% 0%, rgba(216,23,69,0.06), transparent 30%), #f7f8fb',
      }}
    >
      <style>{`
        @keyframes sf-float { 0%,100% { transform: translateY(0) rotate(-1deg); } 50% { transform: translateY(-16px) rotate(1deg); } }
        @keyframes sf-float-slow { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-22px); } }
        @keyframes sf-blob { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(18px,-24px) scale(1.08); } 66% { transform: translate(-16px,12px) scale(0.95); } }
        @keyframes sf-fade-down { 0% { opacity:0; transform: translateY(-18px); } 100% { opacity:1; transform: translateY(0); } }
        @keyframes sf-rise { 0% { opacity:0; transform: translateY(28px); } 100% { opacity:1; transform: translateY(0); } }
        .sf-float { animation: sf-float 7s ease-in-out infinite; }
        .sf-float-slow { animation: sf-float-slow 9s ease-in-out infinite; }
        .sf-blob { animation: sf-blob 16s ease-in-out infinite; }
        .sf-hero-1 { animation: sf-fade-down .7s cubic-bezier(0.16,1,0.3,1) both; }
        .sf-hero-2 { animation: sf-rise .8s cubic-bezier(0.16,1,0.3,1) .1s both; }
        .sf-hero-3 { animation: sf-rise .8s cubic-bezier(0.16,1,0.3,1) .25s both; }
        .sf-hero-4 { animation: sf-rise .8s cubic-bezier(0.16,1,0.3,1) .4s both; }
        .sf-hero-5 { animation: sf-rise .8s cubic-bezier(0.16,1,0.3,1) .55s both; }
        @media (prefers-reduced-motion: reduce) {
          .sf-float,.sf-float-slow,.sf-blob,.sf-hero-1,.sf-hero-2,.sf-hero-3,.sf-hero-4,.sf-hero-5 { animation: none !important; }
        }
      `}</style>

      {/* ============================ HERO ============================ */}
      <section
        className="relative pb-24 pt-28"
        style={{
          background:
            'radial-gradient(circle at 82% 18%, rgba(216,23,69,0.16), transparent 28%), radial-gradient(circle at 8% 82%, rgba(16,21,34,0.08), transparent 25%), linear-gradient(135deg, #ffffff 0%, #f7f8fb 48%, #fff2f5 100%)',
        }}
      >
        <div className="mx-auto grid max-w-[1180px] items-center gap-[60px] lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <span
              className="sf-hero-1 mb-6 inline-block rounded-full px-[18px] py-2.5 text-[15px] font-black"
              style={{ background: ACCENT_SOFT, color: ACCENT, border: '1px solid rgba(216,23,69,0.12)' }}
            >
              Fitness Education, Certification, and Careers
            </span>

            <h1
              className="sf-hero-2 text-[46px] font-bold md:text-[68px]"
              style={{ lineHeight: 1.05, letterSpacing: '-3px' }}
            >
              STAMP <span style={{ color: ACCENT }}>FIT</span>
            </h1>

            <span
              className="sf-hero-3 mb-6 mt-3 inline-block rounded-full px-4 py-2.5 text-[13px] font-black text-white"
              style={{ background: '#111827', letterSpacing: '2.2px', boxShadow: '0 16px 34px rgba(17,24,39,0.16)' }}
            >
              AUTHENTICATE YOUR FITNESS JOURNEY
            </span>

            <p className="max-w-[640px] text-[21px]" style={{ color: MUTED }}>
              피트니스 교육부터 수료 인증, 강사 이력 관리 및 채용 연결까지
              <br />
              전문가 양성부터 성장까지 하나의 플랫폼에서 관리하세요.
            </p>
            <p className="mt-5 text-[21px] font-black" style={{ color: INK }}>
              교육을 찾고, 인증을 남기고, 커리어로 연결하세요.
            </p>

            <div className="sf-hero-5 mt-[30px] flex flex-wrap gap-3.5">
              <button
                type="button"
                onClick={onExploreEducation}
                className="rounded-[14px] px-[26px] py-4 text-white transition-transform duration-300 hover:-translate-y-0.5"
                style={{ background: INK, fontWeight: 900, boxShadow: '0 14px 34px rgba(16,21,34,0.18)' }}
              >
                교육 둘러보기
              </button>
              <button
                type="button"
                onClick={onExploreJobs}
                className="rounded-[14px] bg-white px-[26px] py-4 transition-transform duration-300 hover:-translate-y-0.5"
                style={{ color: INK, fontWeight: 900, border: `1px solid ${LINE}`, boxShadow: '0 10px 26px rgba(16,21,34,0.05)' }}
              >
                채용 둘러보기
              </button>
            </div>
          </div>

          {/* 인증서 비주얼 */}
          <Reveal variant="scale" delay={200}>
            <div
              className="sf-float-slow rounded-[34px] bg-white p-9"
              style={{ border: `1px solid ${LINE}`, boxShadow: '0 30px 80px rgba(17,24,39,0.13)' }}
            >
              <div className="rounded-[24px] bg-white px-[30px] py-7 text-center" style={{ border: `2px solid ${INK}` }}>
                <small className="text-[12px] font-black" style={{ color: ACCENT, letterSpacing: '3px' }}>
                  CERTIFICATE VERIFICATION
                </small>
                <h2 className="my-[18px] text-[36px] font-bold">STAMP FIT</h2>
                <p className="text-[16px]" style={{ color: MUTED }}>
                  교육 기관, 지도자, 그리고 피트니스 시설을 연결하는 신뢰 기반 피트니스 인증 시스템.
                </p>
                <img
                  src="/logo/stampfit-stamp.png"
                  alt="STAMP FIT"
                  className="mx-auto -my-10 h-auto w-full max-w-[260px] object-contain"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================ WHY ============================ */}
      <section className="py-24 text-white" style={{ background: DEEP_GRADIENT }}>
        <Reveal className="mx-auto mb-14 max-w-[760px] text-center">
          <h2 className="text-[34px] font-bold md:text-[42px]" style={{ letterSpacing: '-2px' }}>
            왜 STAMP FIT인가요?
          </h2>
        </Reveal>

        <div className="mx-auto grid max-w-[1180px] gap-6 md:grid-cols-3">
          {[
            {
              title: '1. 피트니스 교육의 아카이브',
              desc: '교정 운동, 기능성 운동, 필라테스 자격과정 등 다양한 피트니스 교육 과정을 한눈에 확인할 수 있습니다.',
            },
            {
              title: '2. 신뢰할 수 있는 교육 수료 인증',
              desc: '교육 기관이 발급한 인증서의 유효성을 QR 코드로 쉽게 검증하고, 파편화된 개인의 교육 이력을 간편하게 관리할 수 있습니다.',
            },
            {
              title: '3. 커리어 연결',
              desc: '지도자는 자신의 교육 이력과 자격을 증명하고, 피트니스 시설은 검증된 인재를 더 쉽게 찾을 수 있습니다.',
            },
          ].map((card, i) => (
            <Reveal key={card.title} delay={i * 120} variant="up">
              <div
                className="h-full rounded-[28px] p-[34px] transition-transform duration-300 hover:-translate-y-2"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <h3 className="mb-4 text-[24px] font-bold">{card.title}</h3>
                <p className="text-[16px]" style={{ color: '#d1d5db' }}>
                  {card.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ====================== CORE FEATURES ====================== */}
      <section className="py-24" style={{ background: '#ffffff' }}>
        <Reveal className="mx-auto mb-14 max-w-[760px] text-center">
          <h2 className="text-[34px] font-bold md:text-[42px]" style={{ letterSpacing: '-2px' }}>
            STAMP FIT의 핵심 기능
          </h2>
          <p className="mt-4 text-[18px]" style={{ color: MUTED }}>
            교육 이력을 단순히 보관하는 것을 넘어, 인증하고 보여주고 활용할 수 있도록 연결합니다.
          </p>
        </Reveal>

        <div className="mx-auto grid max-w-[1180px] gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: '디지털 인증서',
              desc: '교육 기관이 발급한 수료증과 자격 인증서를 디지털 형태로 보관하고, QR 코드를 통해 진위 여부를 간편하게 확인할 수 있습니다.',
              graphic: (
                <div
                  className="sf-float w-[150px] rounded-[18px] bg-white p-5 text-center"
                  style={{ border: `2px solid ${INK}`, boxShadow: '0 14px 30px rgba(17,24,39,0.12)' }}
                >
                  <div className="text-[15px] font-black" style={{ color: ACCENT, letterSpacing: '2px' }}>
                    CERT
                  </div>
                  <div
                    className="mt-5 flex h-[58px] w-[58px] items-center justify-center rounded-[12px] text-[13px] font-black text-white"
                    style={{ background: INK }}
                  >
                    QR
                  </div>
                </div>
              ),
            },
            {
              title: '디지털 커리어 월렛',
              desc: '개인의 교육 이력, 수료 내역, 인증서, 전문 분야를 하나의 커리어 지갑처럼 관리하여 강사의 전문성을 체계적으로 보여줄 수 있습니다.',
              graphic: (
                <div
                  className="sf-float w-[210px] rounded-[20px] p-5"
                  style={{ background: DEEP_GRADIENT, boxShadow: '0 18px 40px rgba(17,24,39,0.25)' }}
                >
                  <div className="h-2.5 w-[64px] rounded-full" style={{ background: ACCENT }} />
                  <div className="mt-9 text-[20px] font-black text-white" style={{ letterSpacing: '1px' }}>
                    CAREER
                  </div>
                </div>
              ),
            },
            {
              title: '레벨 뱃지',
              desc: '특정 교육 수료, 전문 과정 이수, 기관 인증 등 의미 있는 성취를 뱃지로 표시하여 프로필과 채용 과정에서 신뢰도를 높일 수 있습니다.',
              graphic: (
                <div
                  className="sf-float flex h-[140px] w-[140px] items-center justify-center rounded-full"
                  style={{ background: ACCENT, boxShadow: '0 18px 36px rgba(225,29,72,0.3)' }}
                >
                  <div
                    className="flex h-[100px] w-[100px] items-center justify-center rounded-full text-[26px] font-black text-white"
                    style={{ border: '3px solid rgba(255,255,255,0.9)' }}
                  >
                    FIT
                  </div>
                </div>
              ),
            },
            {
              title: 'CAREER PASSPORT',
              desc: '인증된 교육 이력, 수료증, 레벨 뱃지를 하나의 전용 이력서처럼 연결하여 채용 기관이 강사의 전문성을 빠르게 확인할 수 있도록 도와줍니다.',
              graphic: (
                <div
                  className="sf-float relative overflow-hidden rounded-[20px] bg-white"
                  style={{
                    width: 158,
                    height: 168,
                    border: '2px solid #111827',
                    boxShadow: '0 18px 36px rgba(17,24,39,0.14)',
                  }}
                >
                  {/* 증명사진 placeholder */}
                  <div
                    className="absolute rounded-[18px]"
                    style={{
                      top: 18,
                      left: 16,
                      width: 54,
                      height: 70,
                      background: 'linear-gradient(180deg, #f3f4f6, #d1d5db)',
                      border: '1px solid #e5e7eb',
                    }}
                  />
                  {/* 아바타 머리/몸통/하이라이트 */}
                  <div className="absolute z-[1] rounded-full" style={{ top: 28, left: 32, width: 22, height: 22, background: '#111827' }} />
                  <div className="absolute z-[1]" style={{ top: 56, left: 26, width: 34, height: 26, background: '#111827', borderRadius: '18px 18px 8px 8px' }} />
                  <div className="absolute z-[2] rounded-full" style={{ top: 30, left: 46, width: 12, height: 48, background: 'rgba(255,255,255,0.25)', transform: 'rotate(-10deg)' }} />
                  {/* 이력서 라인 */}
                  <div className="absolute rounded-full" style={{ top: 24, left: 82, width: 48, height: 8, background: '#111827' }} />
                  <div className="absolute rounded-full" style={{ top: 42, left: 82, width: 38, height: 8, background: '#e5e7eb' }} />
                  <div className="absolute rounded-full" style={{ top: 64, left: 82, width: 50, height: 8, background: '#e5e7eb' }} />
                  <div className="absolute rounded-full" style={{ top: 100, left: 16, width: 118, height: 8, background: '#e5e7eb' }} />
                  <div className="absolute rounded-full" style={{ top: 120, left: 16, width: 96, height: 8, background: '#e5e7eb' }} />
                  {/* QR */}
                  <div
                    className="absolute"
                    style={{
                      left: 18,
                      bottom: 18,
                      width: 34,
                      height: 34,
                      borderRadius: 6,
                      opacity: 0.9,
                      background:
                        'linear-gradient(90deg, #111 50%, transparent 50%), linear-gradient(#111 50%, transparent 50%)',
                      backgroundSize: '8px 8px',
                    }}
                  />
                  {/* 타이틀 */}
                  <div
                    className="absolute font-black"
                    style={{ left: 58, bottom: 22, color: '#111827', fontSize: 9, letterSpacing: '.8px', lineHeight: 1.2 }}
                  >
                    CAREER
                    <br />
                    PASSPORT
                  </div>
                  {/* VERIFIED 스탬프 */}
                  <div
                    className="absolute flex items-center justify-center font-black"
                    style={{
                      right: 8,
                      bottom: 18,
                      width: 72,
                      height: 72,
                      border: '4px solid #e11d48',
                      borderRadius: '50%',
                      color: '#e11d48',
                      fontSize: 11,
                      letterSpacing: '.8px',
                      transform: 'rotate(-14deg)',
                      background: 'rgba(255,255,255,0.76)',
                    }}
                  >
                    VERIFIED
                  </div>
                </div>
              ),
            },
          ].map((card, i) => (
            <Reveal key={card.title} delay={i * 120} variant="up">
              <div
                className="h-full rounded-[28px] bg-white p-5 transition-transform duration-300 hover:-translate-y-2"
                style={{ border: `1px solid ${LINE}`, boxShadow: '0 16px 44px rgba(0,0,0,0.06)' }}
              >
                <div
                  className="mb-7 flex h-[260px] items-center justify-center overflow-hidden rounded-[24px]"
                  style={{
                    background:
                      'radial-gradient(circle at 78% 18%, rgba(216,23,69,0.14), transparent 40%), linear-gradient(135deg, #fff4f6, #ffffff)',
                    border: '1px solid rgba(216,23,69,0.08)',
                  }}
                >
                  {card.graphic}
                </div>
                <h3 className="mb-3 text-center text-[24px] font-bold">{card.title}</h3>
                <p className="px-2 pb-3 text-center text-[15px]" style={{ color: MUTED }}>
                  {card.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===================== DIGITAL CERTIFICATE ===================== */}
      <FeatureBlock
        eyebrow="Digital Certificate"
        title={
          <>
            종이 수료증은 분실 될 수 있지만,
            <br />
            디지털 인증서는 안전합니다.
          </>
        }
        desc="교육 기관이 발급한 수료증과 자격 인증서를 디지털 형태로 보관하고, QR 코드를 통해 진위 여부를 간편하게 확인할 수 있습니다."
        problem="종이 수료증은 분실되기 쉽고, 진위 여부를 확인하기 어렵습니다. 또한 기관마다 발급 방식이 달라 개인의 교육 이력을 체계적으로 관리하기 어렵습니다."
        solution="인증서를 디지털로 보관하고 QR 코드로 검증할 수 있어, 교육 이력의 신뢰도와 활용성이 높아집니다."
        visual={
          <div
            className="sf-float w-[280px] rounded-[28px] bg-white px-7 py-[34px] text-center"
            style={{ border: `2px solid ${INK}`, boxShadow: '0 24px 50px rgba(17,24,39,0.16)' }}
          >
            <small className="text-[12px] font-black" style={{ color: ACCENT, letterSpacing: '3px' }}>
              DIGITAL CERTIFICATE
            </small>
            <h3 className="my-3 text-[30px] font-bold">STAMP FIT</h3>
            <div className="mx-auto my-2.5 h-2.5 w-[78%] rounded-full" style={{ background: '#e5e7eb' }} />
            <div className="mx-auto my-2.5 h-2.5 w-[55%] rounded-full" style={{ background: '#e5e7eb' }} />
            <img
              src="/logo/stampfit-stamp.png"
              alt="STAMP FIT"
              className="mx-auto -mt-8 -mb-12 h-auto w-full max-w-[230px] object-contain"
            />
          </div>
        }
      />

      {/* ====================== CAREER WALLET ====================== */}
      <FeatureBlock
        reverse
        alt
        eyebrow="Digital Career Wallet"
        title={
          <>
            파편화된 교육 이력을
            <br />
            하나의 커리어 자산으로.
          </>
        }
        desc="개인의 교육 이력, 수료 내역, 그리고 발급된 인증서를 하나의 커리어 월렛에 담아 강사의 전문성을 체계적으로 보여주고 증명할 수 있습니다."
        problem="수료증과 교육 이력이 여러 곳에 흩어져 있어 이력서 작성이나 지원 과정에서 자료를 다시 찾아야 하고, 채용 담당자도 검증하기 어렵습니다."
        solution="모든 교육 이력과 인증 정보를 하나의 프로필로 관리하여, 강사의 성장 과정과 전문성을 한눈에 보여줄 수 있습니다."
        visual={
          <div
            className="sf-float w-[330px] rounded-[34px] p-7 text-white"
            style={{ background: DEEP_GRADIENT, boxShadow: '0 24px 50px rgba(17,24,39,0.2)' }}
          >
            <h3 className="mb-4 text-[26px] font-bold">CAREER WALLET</h3>
            {['Pilates Instructor', 'Functional Training', 'Senior Training', 'Exercise Anatomy'].map((item) => (
              <div
                key={item}
                className="mb-3 rounded-[16px] px-4 py-3.5 text-[15px] font-extrabold"
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <span style={{ color: '#fb7185', marginRight: 8 }}>✓</span>
                {item}
              </div>
            ))}
            <div className="mt-[18px] grid grid-cols-3 gap-2.5">
              {[
                ['42', '교육'],
                ['18', '인증서'],
                ['27', '뱃지'],
              ].map(([n, l]) => (
                <div key={l} className="rounded-[14px] bg-white py-3 text-center text-[13px] font-black" style={{ color: INK }}>
                  {n}
                  <br />
                  {l}
                </div>
              ))}
            </div>
          </div>
        }
      />

      {/* ====================== LEVEL BADGE ====================== */}
      <FeatureBlock
        eyebrow="Level Badge"
        title={
          <>
            전문가로 브랜딩하는
            <br />
            가장 쉬운 방법.
          </>
        }
        desc="지도자의 피트니스 여정을 레벨 뱃지로 시각화하여 전문가로서의 신뢰도를 높일 수 있습니다."
        problem="수료증은 많아도 한눈에 보기 어렵고, 강사의 전문 분야나 성취를 빠르게 판단하기 어렵습니다."
        solution="교육 이수 내역과 전문 역량에 맞춘 레벨 뱃지를 제공하여, 강사의 전문성과 성취를 직관적으로 보여줄 수 있습니다."
        visual={
          <div className="grid grid-cols-2 gap-5">
            {[
              { t: ['LIFE CYCLE', 'FITNESS', 'MASTER'], bg: ACCENT, color: '#fff', border: '6px solid #fff' },
              { t: ['REHAB', 'PILATES', 'SPECIALIST'], bg: INK, color: '#fff', border: '6px solid #fff' },
              { t: ['SENIOR', 'TRAINING', 'SPECIALIST'], bg: '#fff', color: INK, border: `3px solid ${INK}` },
              { t: ['CORRECTIVE', 'EXERCISES', 'CERTIFIED'], bg: ACCENT, color: '#fff', border: '6px solid #fff' },
            ].map((b, i) => (
              <div
                key={b.t.join()}
                className="sf-float flex h-[130px] w-[130px] flex-col items-center justify-center rounded-full text-center text-[12px] font-black leading-tight"
                style={{
                  background: b.bg,
                  color: b.color,
                  border: b.border,
                  boxShadow: '0 18px 36px rgba(225,29,72,0.25)',
                  animationDelay: `${i * 0.8}s`,
                }}
              >
                {b.t.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </div>
            ))}
          </div>
        }
      />

      {/* ====================== AUDIENCE ====================== */}
      <section className="py-24" style={{ background: '#f7f8fb' }}>
        <div className="mx-auto max-w-[1180px] space-y-[46px]">
          {/* For Education Providers */}
          <AudienceBox
            title="피트니스 교육 기관"
            subtitle={
              <>
                교육 기관을 위한 홍보와 수강생 관리,
                <br />
                수료증 발급까지 더 간편하게.
              </>
            }
            desc="교육 기관은 별도의 복잡한 시스템 없이 교육 과정을 등록하고, 신청자 관리부터 수료자 인증까지 하나의 흐름으로 운영할 수 있습니다. STAMP FIT은 기관의 교육 신뢰도와 브랜드 가치를 함께 높여줍니다."
            cta="교육 기관 입점 신청"
            ctaOnClick={onExplorePremium}
            checks={['교육 과정 등록', '신청자 관리', 'QR 인증서 발급', '기관 신뢰도 강화']}
            visual={<ProviderGraphic />}
          />

          {/* For Fitness Instructors (reverse) */}
          <AudienceBox
            reverse
            title="피트니스 강사"
            ctaOnClick={onStartInstructor}
            subtitle={
              <>
                나의 교육 이력과 전문성을
                <br />더 체계적으로 보여주세요.
              </>
            }
            desc="강사는 파편화된 수료증과 교육 이력을 하나의 공간에서 관리할 수 있습니다. 검증된 인증서와 레벨 뱃지를 통해 전문성을 더 명확하게 보여주고, 구직 활동과 커리어 확장에 활용할 수 있습니다."
            cta="강사로 시작하기"
            checks={['교육 과정 신청', '교육 인증서 보관', '프로필 인증', '구직 활동 활용']}
            visual={<InstructorGraphic />}
          />

          {/* For Fitness Facilities */}
          <AudienceBox
            title="피트니스 시설"
            ctaOnClick={onRegisterJob}
            subtitle="검증된 강사와 더 빠르게 연결하세요."
            desc="피트니스 시설은 지원자의 이력과 인증서를 보다 쉽게 확인할 수 있습니다. 단순한 이력서가 아니라 검증 가능한 교육 이력과 전문 분야를 기반으로 신뢰도 높은 채용 결정을 내릴 수 있습니다."
            cta="채용 등록하기"
            checks={['채용 공고 등록', '지원자 이력 확인 및 검증', '주변 검증된 전문가 검색', '신뢰 기반 채용']}
            visual={<FacilityGraphic />}
          />
        </div>
      </section>

      {/* ====================== FAQ ====================== */}
      <section
        id="about-faq"
        className="py-24"
        style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f7f8fb 100%)' }}
      >
        <Reveal className="mx-auto mb-14 max-w-[760px] text-center">
          <h2 className="text-[34px] font-bold md:text-[42px]" style={{ letterSpacing: '-2px' }}>
            자주 묻는 질문
          </h2>
          <p className="mt-4 text-[18px]" style={{ color: MUTED }}>
            STAMP FIT 이용 중 가장 많이 문의하시는 내용을 모았습니다.
          </p>
        </Reveal>

        <div className="mx-auto max-w-[1000px] space-y-[18px]">
          {FAQS.map((faq, i) => (
            <Reveal key={faq.q} delay={i * 60} variant="up">
              <FaqItem question={faq.q}>{faq.a}</FaqItem>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ====================== CTA ====================== */}
      <div id="about-cta" className="mx-auto max-w-[1180px] py-[90px]">
        <Reveal variant="scale">
          <div
            className="rounded-[42px] px-10 py-20 text-center text-white"
            style={{ background: DEEP_GRADIENT, boxShadow: '0 30px 90px rgba(16,21,34,0.20)' }}
          >
            <img
              src="/logo/stampfit-logo-light.png"
              alt="STAMP FIT"
              className="mx-auto mb-8 w-[280px] md:w-[380px]"
            />
            <p className="text-[18px] font-black text-white">Authenticate your fitness journey.</p>
            <p className="mt-3 text-[18px]" style={{ color: '#d1d5db' }}>
              피트니스 교육의 시작부터 커리어의 다음 단계까지,
              <br />
              STAMP FIT이 함께합니다.
            </p>
            <button
              type="button"
              onClick={onExploreEducation}
              className="mt-8 inline-block rounded-[14px] bg-white px-[26px] py-4 transition-transform duration-300 hover:-translate-y-0.5"
              style={{ color: INK, fontWeight: 900 }}
            >
              지금 시작하기
            </button>
          </div>
        </Reveal>
      </div>

      <footer className="bg-white py-[34px] text-center text-[15px]" style={{ color: '#6b7280' }}>
        © 2026 STAMP FIT. All rights reserved.
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FeatureBlock — 디지털 인증서/월렛/뱃지 좌우 분할 섹션                  */
/* ------------------------------------------------------------------ */

interface FeatureBlockProps {
  eyebrow: string;
  title: ReactNode;
  desc: string;
  problem: string;
  solution: string;
  visual: ReactNode;
  reverse?: boolean;
  alt?: boolean;
}

function FeatureBlock({ eyebrow, title, desc, problem, solution, visual, reverse, alt }: FeatureBlockProps) {
  return (
    <section className="py-24" style={{ background: alt ? '#f7f8fb' : '#ffffff' }}>
      <div
        className={`mx-auto grid max-w-[1180px] items-center gap-[60px] lg:grid-cols-2 ${
          reverse ? 'lg:[&>*:first-child]:order-2' : ''
        }`}
      >
        {/* 비주얼 */}
        <Reveal variant={reverse ? 'right' : 'left'} className="flex justify-center">
          <div
            className="relative flex min-h-[420px] w-full items-center justify-center overflow-hidden rounded-[42px]"
            style={{
              background:
                'radial-gradient(circle at 78% 18%, rgba(216,23,69,0.14), transparent 32%), linear-gradient(135deg, #fff4f6, #ffffff)',
              border: '1px solid rgba(216,23,69,0.10)',
              boxShadow: '0 24px 70px rgba(15,23,42,0.08)',
            }}
          >
            <div className="sf-blob pointer-events-none absolute -right-20 -top-20 h-[260px] w-[260px] rounded-full" style={{ background: 'rgba(225,29,72,0.12)' }} />
            <div className="relative z-[1]">{visual}</div>
          </div>
        </Reveal>

        {/* 텍스트 */}
        <Reveal variant={reverse ? 'left' : 'right'} delay={120}>
          <p className="mb-3 font-black" style={{ color: ACCENT }}>
            {eyebrow}
          </p>
          <h2 className="text-[34px] font-bold md:text-[46px]" style={{ lineHeight: 1.2 }}>
            {title}
          </h2>
          <p className="mt-5 text-[18px]" style={{ color: MUTED }}>
            {desc}
          </p>

          <div className="mt-8 grid gap-[18px] sm:grid-cols-2">
            <div className="rounded-[24px] p-6" style={{ background: '#f7f8fb', border: `1px solid ${LINE}` }}>
              <h3 className="mb-2 text-[20px] font-bold">기존 문제점</h3>
              <p className="text-[15px]" style={{ color: MUTED }}>
                {problem}
              </p>
            </div>
            <div className="rounded-[24px] p-6 text-white" style={{ background: DEEP_GRADIENT }}>
              <h3 className="mb-2 text-[20px] font-bold">STAMP FIT의 장점</h3>
              <p className="text-[15px]" style={{ color: '#d1d5db' }}>
                {solution}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  AudienceBox — 대상별 소개 카드 (좌: 타이틀, 우: 비주얼+체크리스트)      */
/* ------------------------------------------------------------------ */

interface AudienceBoxProps {
  title: string;
  subtitle: ReactNode;
  desc: string;
  cta: string;
  checks: string[];
  visual: ReactNode;
  reverse?: boolean;
  /** 있으면 CTA를 앵커 대신 버튼으로 렌더하고 이 핸들러 실행 */
  ctaOnClick?: () => void;
}

function AudienceBox({ title, subtitle, desc, cta, checks, visual, reverse, ctaOnClick }: AudienceBoxProps) {
  const titleCol = (
    <div className={reverse ? 'lg:order-2' : ''}>
      <h2 className="text-[40px] font-bold" style={{ lineHeight: 1.18 }}>
        {title}
      </h2>
      <p className="mt-2 text-[18px]" style={{ color: '#6b7280' }}>
        {subtitle}
      </p>
      <div
        className="mt-4 rounded-[18px] px-[22px] py-5 text-[16px]"
        style={{ background: '#fbfcfe', borderLeft: `5px solid ${ACCENT}`, color: MUTED }}
      >
        {desc}
      </div>
      {ctaOnClick ? (
        <button
          type="button"
          onClick={ctaOnClick}
          className="mt-[18px] inline-block rounded-[14px] px-[26px] py-4 text-white transition-transform duration-300 hover:-translate-y-0.5"
          style={{ background: INK, fontWeight: 900 }}
        >
          {cta}
        </button>
      ) : (
        <a
          href="#about-cta"
          className="mt-[18px] inline-block rounded-[14px] px-[26px] py-4 text-white transition-transform duration-300 hover:-translate-y-0.5"
          style={{ background: INK, fontWeight: 900 }}
        >
          {cta}
        </a>
      )}
    </div>
  );

  const visualCol = (
    <div className={reverse ? 'lg:order-1' : ''}>
      <div
        className="relative flex min-h-[280px] items-center justify-center overflow-hidden rounded-[34px]"
        style={{
          background:
            'radial-gradient(circle at 78% 18%, rgba(216,23,69,0.14), transparent 32%), linear-gradient(135deg, #fff4f6, #ffffff)',
          border: '1px solid rgba(216,23,69,0.10)',
        }}
      >
        <div className="sf-blob pointer-events-none absolute -right-[70px] -top-[70px] h-[220px] w-[220px] rounded-full" style={{ background: 'rgba(225,29,72,0.12)' }} />
        <div className="relative z-[1]">{visual}</div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3.5">
        {checks.map((c, i) => (
          <Reveal
            key={c}
            variant="scale"
            delay={i * 90}
            className="rounded-[18px] p-[18px] text-[15px] font-black transition-transform duration-300 hover:-translate-y-1"
            style={{ background: '#f7f8fb', border: `1px solid ${LINE}` }}
          >
            <span style={{ color: ACCENT, marginRight: 8 }}>✓</span>
            {c}
          </Reveal>
        ))}
      </div>
    </div>
  );

  return (
    <Reveal variant="up">
      <div
        className="grid items-center gap-[42px] rounded-[42px] bg-white p-[38px] sm:p-[62px] lg:grid-cols-[0.85fr_1.15fr]"
        style={{ border: `1px solid ${LINE}`, boxShadow: '0 26px 80px rgba(15,23,42,0.09)' }}
      >
        {reverse ? (
          <>
            {visualCol}
            {titleCol}
          </>
        ) : (
          <>
            {titleCol}
            {visualCol}
          </>
        )}
      </div>
    </Reveal>
  );
}

/* ------------------------------------------------------------------ */
/*  Audience 비주얼 목업 (원본 CSS 그래픽 재현)                           */
/* ------------------------------------------------------------------ */

function ScreenLine({ w }: { w: string }) {
  return <div className="mb-3 h-2.5 rounded-full" style={{ width: w, background: '#e5e7eb' }} />;
}

function ProviderGraphic() {
  return (
    <div className="relative w-[280px]">
      <div className="relative overflow-hidden rounded-[24px] bg-white p-[22px]" style={{ border: `2px solid ${INK}`, boxShadow: '0 20px 45px rgba(17,24,39,0.15)' }}>
        <div className="mb-[18px] h-3 w-[90px] rounded-full" style={{ background: ACCENT }} />
        <ScreenLine w="100%" />
        <ScreenLine w="75%" />
        <ScreenLine w="50%" />
        <ScreenLine w="100%" />
        {/* 문서 안쪽 우측 하단에 자리잡은 QR 코드 아이콘 */}
        <QrCode className="absolute bottom-3 right-3 h-16 w-16" style={{ color: INK, opacity: 0.9 }} />
      </div>
      {/* 문서 위로 겹쳐지는 QR CERT 카드 */}
      <div
        className="absolute -bottom-2.5 -right-2 z-10 flex h-[115px] w-[95px] items-center justify-center rounded-[16px] bg-white text-center text-[12px] font-black"
        style={{ border: `2px solid ${INK}`, color: ACCENT, boxShadow: '0 14px 30px rgba(17,24,39,0.14)' }}
      >
        QR
        <br />
        CERT
      </div>
    </div>
  );
}

function InstructorGraphic() {
  return (
    <div className="w-[280px]">
      <div className="rounded-[28px] p-[26px] text-white" style={{ background: DEEP_GRADIENT, boxShadow: '0 20px 45px rgba(17,24,39,0.2)' }}>
        <div className="mb-[18px] flex h-[74px] w-[74px] items-center justify-center rounded-full text-[34px]" style={{ background: ACCENT }}>
          👤
        </div>
        <h3 className="mb-4 text-[24px] font-bold">Instructor Profile</h3>
        <ScreenLine w="100%" />
        <ScreenLine w="75%" />
        <div className="mt-[18px] flex gap-2">
          {['CERT', 'BADGE', 'CAREER'].map((b) => (
            <div key={b} className="flex-1 rounded-full py-2 text-center text-[12px] font-black" style={{ background: 'rgba(255,255,255,0.12)' }}>
              {b}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FacilityGraphic() {
  return (
    <div className="w-[280px]">
      <div className="rounded-[28px] bg-white p-[22px]" style={{ border: `2px solid ${INK}`, boxShadow: '0 20px 45px rgba(17,24,39,0.15)' }}>
        {[
          ['지원자 A', 'VERIFIED'],
          ['지원자 B', 'QR CHECK'],
          ['지원자 C', 'BADGE'],
        ].map(([name, tag]) => (
          <div
            key={name}
            className="mb-3 flex items-center justify-between rounded-[16px] p-3.5 text-[15px] font-black"
            style={{ background: '#f7f8fb', border: `1px solid ${LINE}` }}
          >
            {name}
            <span className="text-[13px]" style={{ color: ACCENT }}>
              {tag}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ 데이터                                                          */
/* ------------------------------------------------------------------ */

const FAQS: { q: string; a: ReactNode }[] = [
  {
    q: '회원 가입 시 문자 수신에 동의하면 어떤 혜택이 있나요?',
    a: (
      <div className="space-y-4">
        <p>
          STAMP FIT에 입점한 우수 인증 교육 기관 및 일반 교육 기관은 연 1회 이상 무료 교육 또는 특별
          프로모션을 진행하도록 권장하고 있습니다.
        </p>
        <p>
          또한 STAMP FIT 회원을 대상으로 교육 할인, 특별 이벤트, 우선 신청 기회 등 다양한 혜택이 제공될
          예정입니다. 이러한 혜택 및 안내는 문자 수신에 동의한 회원에게만 제공됩니다.
        </p>
      </div>
    ),
  },
  {
    q: '우수 인증 교육 기관은 어떤 기준으로 선정되나요?',
    a: (
      <div className="space-y-2">
        <p>우수 인증 교육 기관은 다음과 같은 정량적·정성적 기준을 종합적으로 평가하여 선정됩니다.</p>
        <h4 className="pt-2 text-[18px] font-bold" style={{ color: INK }}>
          정량적 평가
        </h4>
        <ul className="list-disc space-y-2 pl-[22px]">
          <li>교육 운영 기간</li>
          <li>총 강의 회차</li>
          <li>누적 수강생 수</li>
          <li>전용 교육 공간 보유 여부</li>
          <li>정규 교육 과정 운영 여부</li>
          <li>소속 강사 수</li>
        </ul>
        <h4 className="pt-2 text-[18px] font-bold" style={{ color: INK }}>
          정성적 평가
        </h4>
        <ul className="list-disc space-y-2 pl-[22px]">
          <li>교육 콘텐츠의 전문성 및 깊이</li>
          <li>교육 운영 역량</li>
          <li>브랜드 신뢰도 및 업계 평판</li>
        </ul>
        <p className="pt-2">우수 인증 교육 기관은 매년 재평가를 통해 갱신되며, 신규 신청은 연중 상시 가능합니다.</p>
      </div>
    ),
  },
  {
    q: '인증서 디자인은 등록된 양식만 사용할 수 있나요?',
    a: (
      <div className="space-y-4">
        <p>현재 STAMP FIT에서는 약 20종 이상의 인증서 디자인을 제공하고 있습니다.</p>
        <p>
          교육 기관만의 고유한 인증서 디자인이 필요한 경우, 별도의 디지털 인증서 등록 서비스(유료)를 통해
          맞춤형 인증서를 사용할 수 있습니다.
        </p>
        <p>등록된 맞춤형 인증서는 해당 교육 기관만 열람 및 사용할 수 있습니다.</p>
      </div>
    ),
  },
  {
    q: '이력서에 인증되지 않은 교육 이력이 있으면 허위 이력인가요?',
    a: (
      <div className="space-y-3">
        <p>아닙니다.</p>
        <p>STAMP FIT의 교육 이력 인증은 현재 STAMP FIT에 입점한 교육 기관의 수료 정보와 연동하여 제공됩니다.</p>
        <p>또한 국가자격증, 학위증명서(대학교·대학원)는 증빙서류 확인 후 인증 표시가 가능합니다.</p>
        <p>인증 표시가 없는 교육 이력은 단순히 진위 여부를 확인하지 못한 상태를 의미하며, 허위 이력을 뜻하지는 않습니다.</p>
        <strong className="block pt-2 font-bold" style={{ color: INK }}>
          ① 교육 기관이 STAMP FIT에 입점해 있으나 교육 이력이 누락된 경우
        </strong>
        <ul className="list-disc space-y-2 pl-[22px]">
          <li>해당 교육 기관에 수료 정보 등록을 요청해 주시기 바랍니다.</li>
        </ul>
        <strong className="block pt-2 font-bold" style={{ color: INK }}>
          ② 교육 기관이 STAMP FIT에 입점해 있지 않은 경우
        </strong>
        <ul className="list-disc space-y-2 pl-[22px]">
          <li>해당 교육 기관에 STAMP FIT 입점을 제안해 주시기 바랍니다.</li>
          <li>또한 STAMP FIT 고객센터에 교육 기관명과 교육 과정을 알려주시면, 다수의 요청이 접수될 경우 별도로 입점을 제안할 수 있습니다.</li>
        </ul>
      </div>
    ),
  },
  {
    q: '현재 구직 중이 아닌데 채용 제안 메시지가 계속 옵니다. 차단할 수 있나요?',
    a: (
      <div className="space-y-4">
        <p>가능합니다.</p>
        <p>내 정보 → 이력서 관리 메뉴에서 구직 희망 상태(On / Off) 를 설정할 수 있습니다.</p>
        <p>
          구직 희망 상태가 “ON”인 경우에만 채용 기관이 이력서를 열람하거나 제안 메시지를 보낼 수 있으며,
          구직 활동을 원하지 않는 경우 “OFF”로 변경하시기 바랍니다.
        </p>
      </div>
    ),
  },
  {
    q: '인증서를 발급한 교육 기관이 폐업하거나 운영을 종료하면 인증서는 어떻게 되나요?',
    a: (
      <div className="space-y-4">
        <p>STAMP FIT에 등록된 인증서는 디지털 인증 형태로 별도 저장 및 관리됩니다.</p>
        <p>따라서 발급 기관의 운영 여부와 관계없이 회원은 자신의 교육 참여 이력 및 인증서를 지속적으로 확인할 수 있습니다.</p>
        <p>단, 서비스 정책 및 운영 환경에 따라 제공 방식은 변경될 수 있습니다.</p>
      </div>
    ),
  },
  {
    q: '디지털 월렛과 뱃지를 개인 확인 외에도 활용할 수 있나요?',
    a: (
      <div className="space-y-4">
        <p>네, 가능합니다.</p>
        <p>STAMP FIT은 강사용 프로필 전용 QR 코드를 제공합니다.</p>
        <p>해당 QR 코드를 통해 인증된 교육 이력, 보유 자격, 획득한 뱃지 등을 확인할 수 있으며, 개인 웹사이트, SNS, 명함, 시설 내 게시물 등에 활용할 수 있습니다.</p>
        <p>이를 통해 회원, 시설 관리자 및 채용 담당자에게 전문성을 효과적으로 전달하고 개인 브랜딩에도 도움을 받을 수 있습니다.</p>
      </div>
    ),
  },
];
